/**
 * IANA timezone options for the event form's Timezone select.
 *
 * Built from the runtime's own zone database (`Intl.supportedValuesOf`) rather
 * than a checked-in list — a hardcoded list goes stale as zones are added,
 * renamed or split, and the runtime already ships the authoritative copy.
 *
 * The account-scoped `typeActions.timezones` fetch is deliberately NOT reused:
 * it keys zones by COUNTRY, and an event's country is optional, so a
 * country-scoped list would come back empty for the many events authored
 * without an address.
 */
import { FALLBACK_TIMEZONES } from './fallbackTimezones'

/**
 * Zone list from the runtime, falling back to the bundled list.
 *
 * `Intl.supportedValuesOf` is absent on Hermes (React Native), where returning
 * `[]` would render an empty picker — so the fallback is load-bearing on
 * mobile, not just defensive. A browser gets the runtime's own, always-current
 * database.
 */
const supportedZones = (): string[] => {
  try {
    const supportedValuesOf = (
      Intl as unknown as { supportedValuesOf?: (key: string) => string[] }
    ).supportedValuesOf

    if (typeof supportedValuesOf !== 'function') {
      return [...FALLBACK_TIMEZONES]
    }

    const zones = supportedValuesOf('timeZone')

    return zones && zones.length > 0 ? zones : [...FALLBACK_TIMEZONES]
  } catch {
    return [...FALLBACK_TIMEZONES]
  }
}

/** The parts of a formatted instant needed to rebuild it as a UTC timestamp. */
const zonedParts = (timezone: string, at: Date): Record<string, string> =>
  new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
    .formatToParts(at)
    .reduce((acc, part) => {
      if (part.type !== 'literal') {
        acc[part.type] = part.value
      }
      return acc
    }, {} as Record<string, string>)

/**
 * The wall-clock a zone shows at `at`, re-read as though it were UTC.
 *
 * Diffing this against the real instant is what yields the offset — there is no
 * direct "offset for zone" API. `hour: '2-digit'` with `hour12: false` renders
 * midnight as '24' in some runtimes, which `Date.UTC` would roll into the next
 * day, so it is normalized back to 0.
 */
const zonedTimeAsUtc = (timezone: string, at: Date): number => {
  const parts = zonedParts(timezone, at)
  const hour = Number(parts.hour) % 24

  return Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    hour,
    Number(parts.minute),
    Number(parts.second)
  )
}

/**
 * Current UTC offset for a zone, in minutes east of UTC.
 *
 * DST-sensitive by design: America/Vancouver reads -420 (GMT-7) in August and
 * -480 (GMT-8) in January, matching what the admin's clock actually says when
 * they pick it.
 *
 * Returns null for a zone the runtime cannot format, so callers drop it rather
 * than label it wrongly.
 */
export const getZoneOffsetMinutes = (
  timezone: string,
  at: Date = new Date()
): number | null => {
  try {
    // Seconds are dropped: no real zone has a sub-minute offset today, and
    // rounding avoids a spurious ±1 from the two formats landing either side
    // of a second boundary.
    return Math.round((zonedTimeAsUtc(timezone, at) - at.getTime()) / 60000)
  } catch {
    return null
  }
}

/** '-420' → 'GMT-7', '330' → 'GMT+5:30', '0' → 'GMT+0'. */
export const formatZoneOffset = (offsetMinutes: number): string => {
  const sign = offsetMinutes < 0 ? '-' : '+'
  const abs = Math.abs(offsetMinutes)
  const hours = Math.floor(abs / 60)
  const minutes = abs % 60

  return minutes === 0
    ? `GMT${sign}${hours}`
    : `GMT${sign}${hours}:${String(minutes).padStart(2, '0')}`
}

/**
 * Display label for a zone: `America/Toronto (GMT-4)`.
 *
 * The IANA name leads because it is what the admin is actually choosing and
 * what gets stored; the offset trails as the disambiguator between the many
 * zones that share one.
 */
export const formatZoneLabel = (
  timezone: string,
  offsetMinutes: number
): string => `${timezone} (${formatZoneOffset(offsetMinutes)})`

export interface TimezoneOption {
  label: string
  value: string
}

/**
 * Memo of the last built list, keyed by `extraZone`.
 *
 * Building the list costs ~420 `Intl.DateTimeFormat` constructions, which is
 * far too expensive to repeat per render — unmemoized on mobile it blocked the
 * JS thread badly enough to make the form's buttons unresponsive. The form
 * calls this on every render with a stable `extraZone`, so a single-entry cache
 * turns all but the first into a lookup.
 *
 * `at` is deliberately NOT part of the key: it exists for tests, and offsets
 * only shift at DST boundaries — rebuilding the whole list to catch one is not
 * worth making every render pay. A picker open across a DST transition shows
 * the pre-transition offset until remount, which is cosmetic.
 */
let cache: { key: string; options: TimezoneOption[] } | null = null

/** The runtime's zone set, resolved once. */
let knownZonesCache: Set<string> | null = null

const knownZones = (): Set<string> => {
  if (!knownZonesCache) {
    knownZonesCache = new Set(supportedZones())
  }

  return knownZonesCache
}

/**
 * The zone name the runtime canonicalizes `timezone` to, or the input when it
 * cannot be resolved.
 *
 * IANA keeps renamed zones working as aliases, and the two spellings do NOT
 * both appear in the zone list: ICU lists `Asia/Calcutta` but not
 * `Asia/Kolkata`, `Europe/Kiev` but not `Europe/Kyiv`. An account stored under
 * the modern spelling would otherwise match no option and the select would
 * render its placeholder instead of the account's own zone.
 */
export const canonicalizeZone = (timezone: string): string => {
  try {
    return (
      new Intl.DateTimeFormat('en-US', { timeZone: timezone }).resolvedOptions()
        .timeZone || timezone
    )
  } catch {
    return timezone
  }
}

/**
 * The option value that represents `timezone` in the built list.
 *
 * Callers bind their select to this rather than to the raw stored value, so an
 * aliased zone still shows as selected. Returns '' for an empty input.
 */
export const resolveZoneOptionValue = (timezone?: string | null): string => {
  const zone = timezone?.trim() ?? ''

  if (zone === '') {
    return ''
  }

  const known = knownZones()

  if (known.has(zone)) {
    return zone
  }

  const canonical = canonicalizeZone(zone)

  return known.has(canonical) ? canonical : zone
}

/**
 * Every zone the runtime knows, ordered west-to-east by current offset and
 * alphabetically within an offset, so the list reads like a map rather than a
 * flat A-Z of continents.
 *
 * `extraZone` guarantees the currently-stored zone is present even when the
 * runtime does not list it — an event authored against a since-renamed zone
 * (or a zone list the browser trims) must still show its own value rather than
 * silently rendering an empty select.
 *
 * Result is cached — see `cache` above. Safe to call on every render.
 */
export const buildTimezoneOptions = (
  extraZone?: string | null,
  at: Date = new Date()
): TimezoneOption[] => {
  const extra = extraZone?.trim() ?? ''
  // The extra zone only widens the list when the runtime does not already know
  // it — which is the rare case. Keying the cache on "did we actually have to
  // add something" instead of on the zone itself means SELECTING a zone (the
  // common path, where the value is already in the list) is a cache hit rather
  // than a full ~420-zone rebuild.
  const known = knownZones()
  // Resolved through the alias table first, so a zone the runtime knows under
  // another name is a cache HIT rather than an extra entry.
  const resolved = extra === '' ? '' : resolveZoneOptionValue(extra)
  const needsExtra = resolved !== '' && !known.has(resolved)
  const key = needsExtra ? resolved : ''

  if (cache && cache.key === key) {
    return cache.options
  }

  const zones = new Set(known)

  if (needsExtra) {
    zones.add(resolved)
  }

  const options = Array.from(zones)
    .map((zone) => {
      const offsetMinutes = getZoneOffsetMinutes(zone, at)
      return offsetMinutes === null ? null : { zone, offsetMinutes }
    })
    .filter((entry): entry is { zone: string; offsetMinutes: number } =>
      Boolean(entry)
    )
    .sort(
      (a, b) =>
        a.offsetMinutes - b.offsetMinutes || a.zone.localeCompare(b.zone)
    )
    .map(({ zone, offsetMinutes }) => ({
      label: formatZoneLabel(zone, offsetMinutes),
      value: zone,
    }))

  cache = { key, options }

  return options
}
